import { useEffect } from "react";

import { Navigate, useNavigate, useRoutes } from "react-router-dom";

import { ERole } from "./@types";
import { apis, callApi } from "./api";
import {
	AuthGuard,
	GuestGuard,
	PremiumGuard,
	RoleBasedGuard,
} from "./components/common/Guard";
import About from "./components/paginas/About/About";
import { AboutMe } from "./components/paginas/AboutMe/AboutMe";
import { BillDetail } from "./components/paginas/BillDetail/BillDetail";
import { CreateAccount } from "./components/paginas/CreateAccount/CreateAccount";
import { FlightRecorder } from "./components/paginas/FlightRecorder/FlightRecorder";
import GuestHome from "./components/paginas/Home/Home";
import { GoogleAuthenticationSuccess } from "./components/paginas/Instructor/GoogleAuthentication/GoogleAuthenticationSuccess";
import { Stripe } from "./components/paginas/Instructor/Stripe/Stripe";
import { StripeSuccess } from "./components/paginas/Instructor/StripeSuccess/StripeSuccess";
import { Learning } from "./components/paginas/Learning/Learning";
import { Login } from "./components/paginas/LogIn/Login";
import { MyCourseInformation } from "./components/paginas/MyCourseInformation/MyCourseInformation";
import { OrkaMarketplace } from "./components/paginas/OrkaMarketplace/OrkaMarketplace";
import { OrkaSoftware } from "./components/paginas/OrkaSoftware/OrkaSoftware";
import { Pricing } from "./components/paginas/Pricing/Pricing";
import { PrivacyPolicy } from "./components/paginas/PrivacyPolicy/PrivacyPolicy";
import { PublicProfile } from "./components/paginas/PublicProfile/PublicProfile";
import { ResetPassword } from "./components/paginas/ResetPassword/ResetPassword";
import { Resources } from "./components/paginas/Resources/Resources";
import { BecomeInstructor } from "./components/paginas/Student/BecomeInstructor/BecomeInstructor";
import { Chat } from "./components/paginas/Student/Chat/Chat";
import { FlightLogs } from "./components/paginas/Student/FlightLogs/FlightLogs";
import { Teaching } from "./components/paginas/Teaching/Teaching";
import { TeachingPreview } from "./components/paginas/TeachingPreview/TeachingPreview";
import { TermsAndConditions } from "./components/paginas/TermsAndConditions/TermsAndConditions";
import { ThreeDMap } from "./components/paginas/ThreeDMap/ThreeDMap";
import { Home } from "./components/paginas/Training/Training";
import { CheckoutCourse } from "./components/paginas/User/CheckoutCourse/CheckoutCourse";
import { PreviewCourse } from "./components/paginas/User/PreviewCourse/PreviewCourse";
import { AccountService, useAccount } from "./hooks/useAccount";
import { useChatInit } from "./hooks/useChat";
import { usePaymentRequestInit } from "./hooks/usePaymentRequest";
import { usePremiumPackageInit } from "./hooks/usePremiumPackage";
import { useProfileInit } from "./hooks/useProfile";
import { StripeService } from "./hooks/useStripe";
import { TokenService, useToken } from "./hooks/useToken";

import HowItWorks from "./components/paginas/HowItWorks/HowItWorks";
import "./styles/autocomplete.scss";
import "./styles/button.scss";
import "./styles/calendar.scss";
import "./styles/dropzone.scss";
import "./styles/icon.scss";
import "./styles/input.scss";
import "./styles/popover.scss";
import "./styles/scroll_position.scss";
import "./styles/select.scss";
import "./styles/separator.scss";
import "./styles/text.scss";

function App() {
	const { token } = useToken();
	const { account } = useAccount();
	const navigate = useNavigate();
	usePaymentRequestInit();
	useChatInit({ chatScreen: false, getMessages: false });
	useProfileInit();
	usePremiumPackageInit();

	useEffect(() => {
		const getUserInfoByToken = async () => {
			try {
				const response = await callApi({
					method: "GET",
					url: apis.GET_USER_BY_TOKEN,
				});
				const { stripeInfo, ...user } = response;
				AccountService.saveAccount(user);
				StripeService.saveStripeData(stripeInfo);
			} catch (error: any) {
				if (error?.response?.status === 404) {
					TokenService.clearToken();
					navigate("/", { replace: true });
				}
			}
		};

		// If can get token from local storage but did not get user info yet
		// just call api to get user info
		if (token && !account) {
			getUserInfoByToken();
		}
	}, [account, navigate, token]);

	const routes = useRoutes([
		{
			path: "/",
			element: <GuestHome />,
		},
		{
			path: "/how-it-works",
			element: <HowItWorks />,
		},
		{
			path: "/marketplace",
			element: <OrkaMarketplace />,
		},
		{
			path: "/terms-and-conditions",
			element: <TermsAndConditions />,
		},
		{
			path: "/privacy-policy",
			element: <PrivacyPolicy />,
		},
		{
			path: "/sign-in",
			element: (
				<GuestGuard>
					<Login />
				</GuestGuard>
			),
		},
		{
			path: "/sign-up",
			element: (
				<GuestGuard>
					<CreateAccount />
				</GuestGuard>
			),
		},
		{
			path: "/reset-password/:token",
			element: (
				<GuestGuard>
					<ResetPassword />
				</GuestGuard>
			),
		},
		{
			path: "/about-us",
			element: <About />,
		},
		{
			path: "/training",
			children: [
				"dashboard",
				"my-services",
				"my-progress",
				"calendar",
				"payments",
				"availability",
			].map((path) => ({
				path,
				element: (
					<AuthGuard>
						<RoleBasedGuard accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
							<Home />
						</RoleBasedGuard>
					</AuthGuard>
				),
			})),
		},
		{
			path: "/about-me",
			children: ["account", "profile", "safety"].map((path) => ({
				path,
				element: (
					<AuthGuard>
						<RoleBasedGuard accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
							<AboutMe />
						</RoleBasedGuard>
					</AuthGuard>
				),
			})),
		},
		{
			path: "/learning",
			children: [
				{
					path: ":courseSlug",
					element: (
						<AuthGuard>
							<RoleBasedGuard
								accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
								<Learning />
							</RoleBasedGuard>
						</AuthGuard>
					),
				},
			],
		},
		{
			path: "/teaching",
			children: [
				{
					path: ":courseSlug/:studentId",
					element: (
						<AuthGuard>
							<RoleBasedGuard accessibleRoles={[ERole.INSTRUCTOR]}>
								<Teaching />
							</RoleBasedGuard>
						</AuthGuard>
					),
				},
			],
		},
		{
			path: "/my-services",
			children: [
				{
					path: ":slug",
					children: [
						"description",
						"classes",
						"logistics",
						"publishing",
						"remove",
						"students",
					]
						.map((path) => ({
							path,
							element: (
								<AuthGuard>
									<RoleBasedGuard accessibleRoles={[ERole.INSTRUCTOR]}>
										<MyCourseInformation />
									</RoleBasedGuard>
								</AuthGuard>
							),
						}))
						.concat({
							path: "preview",
							element: (
								<AuthGuard>
									<RoleBasedGuard accessibleRoles={[ERole.INSTRUCTOR]}>
										<TeachingPreview />
									</RoleBasedGuard>
								</AuthGuard>
							),
						}),
				},
			],
		},
		{
			path: "/stripe",
			children: [
				{
					path: "",
					element: (
						<AuthGuard>
							<RoleBasedGuard
								accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
								<Stripe />
							</RoleBasedGuard>
						</AuthGuard>
					),
				},
				{
					path: "success",
					element: (
						<AuthGuard>
							<RoleBasedGuard
								accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
								<StripeSuccess />
							</RoleBasedGuard>
						</AuthGuard>
					),
				},
				{
					path: "refresh",
					element: (
						<AuthGuard>
							<RoleBasedGuard accessibleRoles={[ERole.INSTRUCTOR]}>
								<StripeSuccess />
							</RoleBasedGuard>
						</AuthGuard>
					),
				},
			],
		},
		{
			path: "/services",
			children: [
				{
					path: ":slug",
					children: [
						{
							path: "",
							element: <PreviewCourse />,
						},
						{
							path: "checkout",
							element: (
								<AuthGuard>
									<CheckoutCourse />
								</AuthGuard>
							),
						},
					],
				},
			],
		},
		{
			path: "/messages",
			children: [
				{
					path: "",
					element: (
						<AuthGuard>
							<Chat />
						</AuthGuard>
					),
				},
				{
					path: ":messagesUserId",
					element: (
						<AuthGuard>
							<Chat />
						</AuthGuard>
					),
				},
			],
		},
		{
			path: "/auth",
			children: [
				{
					path: "google",
					children: [
						{
							path: "success",
							element: (
								<AuthGuard>
									<GoogleAuthenticationSuccess />
								</AuthGuard>
							),
						},
					],
				},
			],
		},
		{
			path: "/become-instructor",
			element: (
				<AuthGuard>
					<RoleBasedGuard accessibleRoles={[ERole.STUDENT]}>
						<BecomeInstructor />
					</RoleBasedGuard>
				</AuthGuard>
			),
		},
		{
			path: "/flight-logs",
			children: [
				{
					path: "",
					element: (
						<AuthGuard>
							<PremiumGuard>
								<RoleBasedGuard
									accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
									<FlightLogs />
								</RoleBasedGuard>
							</PremiumGuard>
						</AuthGuard>
					),
				},
				{
					path: ":id",
					element: (
						<AuthGuard>
							<PremiumGuard>
								<RoleBasedGuard
									accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
									<FlightLogs />
								</RoleBasedGuard>
							</PremiumGuard>
						</AuthGuard>
					),
				},
			],
		},
		{
			path: "/payment-requests",
			children: [
				{
					path: ":billId",
					element: (
						<AuthGuard>
							<RoleBasedGuard
								accessibleRoles={[ERole.STUDENT, ERole.INSTRUCTOR]}>
								<BillDetail />
							</RoleBasedGuard>
						</AuthGuard>
					),
				},
			],
		},
		{
			path: "/software",
			element: <OrkaSoftware />,
		},
		{
			path: "/profile",
			children: [
				{
					path: ":profileSlug",
					element: <PublicProfile />,
				},
			],
		},
		{
			path: "/resources",
			children: [
				{
					path: "",
					element: <Resources />,
				},
				{
					path: ":resourceSlug",
					element: <Resources />,
				},
			],
		},
		{
			path: "/3d-map",
			element: <ThreeDMap />,
		},
		{
			path: "/fly-orka-app",
			element: <FlightRecorder />,
		},
		{
			path: "/memberships",
			element: <Pricing />,
		},
		{
			path: "*",
			element: <Navigate to="/" replace />,
		},
	]);

	return routes;
}

export default App;
